import React from 'react';

import Section from '@hero/ui-kit/layout/Section';
import ErrorState from '../../../../components/States/Error';
import Loading from '../../../../components/States/Loading';
import { useOrganizationMemberDetailsContext } from '../context/OrganizationMemberDetailsContext';
import MemberDetailsWrapper from '../MemberDetailsWrapper';
import useGetEnrollmentHistory from './api/useGetEnrollmentHistory';
import useGetMemberClinicalData from './api/useGetMemberClinicalData';
import useGetPortalLink from './api/useGetPortalLink';
import Allergies from './components/Allergies';
// import AppointmentNeeded from './components/AppointmentNeeded';
import Conditions from './components/Conditions';
import Enrollment from './components/Enrollment';
import Medications from './components/Medications';
import Reference from './components/Reference';
import Tasks from './components/Tasks';
import * as Style from './style.module.scss';
import { hasEhrRecord } from './utils/clinicalSummary';
import { formatLastSynced, getLastSyncedAt } from './utils/formatters';

const EhrRecord: React.FC = () => {
    const { memberDetails, medications } = useOrganizationMemberDetailsContext();
    const memberInfo = memberDetails?.member_info;
    const memberId = memberInfo?.member_id;

    const { data, isLoading, isError } = useGetMemberClinicalData(memberId);
    const { data: enrollmentHistory, isLoading: isEnrollmentLoading } =
        useGetEnrollmentHistory(memberId);
    const { data: portalLink, isLoading: isPortalLinkLoading } = useGetPortalLink(memberId);

    const heroMedications = React.useMemo(
        () => [...medications.inside, ...medications.outside],
        [medications]
    );

    if (isLoading) {
        return <Loading height={15} />;
    }

    if (isError) {
        return <ErrorState errorMessage="We could not load this member's clinical record" />;
    }

    if (!hasEhrRecord(data)) {
        return <ErrorState errorMessage="No clinical record is available for this member" />;
    }

    const lastSynced = (records: { last_synced_at: string | null }[]) =>
        formatLastSynced(getLastSyncedAt(records, data.last_synced_at));

    return (
        <div className={Style.layout}>
            <div className={Style.column}>
                <Conditions conditions={data.conditions} lastSynced={lastSynced(data.conditions)} />
                <Allergies allergies={data.allergies} lastSynced={lastSynced(data.allergies)} />
                <Tasks memberId={memberId} />
                {/*<AppointmentNeeded memberId={memberId} flags={data.appointment_needed} />*/}
            </div>

            <div className={Style.column}>
                <Medications
                    medications={data.medications}
                    heroMedications={heroMedications}
                    lastSynced={lastSynced(data.medications)}
                />
                <Enrollment
                    entries={enrollmentHistory ?? []}
                    flags={data.flags}
                    isLoading={isEnrollmentLoading}
                />
                <Reference
                    memberInfo={memberInfo}
                    portalLink={portalLink ?? null}
                    isPortalLinkLoading={isPortalLinkLoading}
                />
            </div>
        </div>
    );
};

const EhrRecordWrapped: React.FC = () => (
    <MemberDetailsWrapper>
        <Section noDefaultPadding paddingHorizontal="regular" paddingTop="small">
            <EhrRecord />
        </Section>
    </MemberDetailsWrapper>
);

export default EhrRecordWrapped;
